import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useGetAllAssembliesQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { abyssColors } from '@/theme/colors';
import { useWizard } from '../context/WizardContext';
import { Assembly, FileMapping } from '../types';

const stripExtension = (fileName: string) => fileName.replace(/\.[^/.]+$/, '');

const autoMap = (files: File[], assemblies: Assembly[]): FileMapping[] => {
  return files.map((file) => {
    const baseName = stripExtension(file.name).toLowerCase();
    const match = assemblies.find((assembly) => assembly.tagName.toLowerCase() === baseName);
    return {
      file,
      assemblyId: match?.id ?? null,
      tagName: match?.tagName ?? null,
    };
  });
};

export const MapAssemblies = () => {
  const { selectedFiles, fileMappings, setFileMappings, goToNextStep, goToPreviousStep } =
    useWizard();
  const structureId = useGetStructureId();

  const { data, loading } = useGetAllAssembliesQuery({ variables: { structureId } });

  const assemblies: Assembly[] = (data?.allAssemblies ?? []).map((assembly) => ({
    id: assembly.id,
    tagName: assembly.tagName,
  }));

  // Auto-map on first load or when files change
  const [hasAutoMapped, setHasAutoMapped] = useState(false);
  useEffect(() => {
    if (!loading && assemblies.length > 0 && !hasAutoMapped) {
      setFileMappings(autoMap(selectedFiles, assemblies));
      setHasAutoMapped(true);
    }
  }, [loading, assemblies.length, hasAutoMapped]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMapping = (index: number, assembly: Assembly | null) => {
    const updated = fileMappings.map((mapping, mappingIndex) =>
      mappingIndex === index
        ? { ...mapping, assemblyId: assembly?.id ?? null, tagName: assembly?.tagName ?? null }
        : mapping
    );
    setFileMappings(updated);
  };

  const allMapped =
    fileMappings.length > 0 && fileMappings.every((mapping) => mapping.assemblyId !== null);

  return (
    <Stack gap="2.4rem" height="100%" flex={1} minWidth="120rem">
      <Stack gap="0.4rem">
        <Typography variant="body1" color="primary.600" fontWeight={500}>
          Map files to assemblies
        </Typography>
        <Typography variant="body2" color="primary.400">
          Files have been auto-matched by name. Select an assembly for any unmatched files.
        </Typography>
      </Stack>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Stack sx={{ overflowY: 'auto', flex: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: abyssColors.primary[50] }}>
                  File
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: abyssColors.primary[50] }}>
                  Assembly tag
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fileMappings.map((mapping, index) => (
                <TableRow key={`${mapping.file.name}-${index}`}>
                  <TableCell>
                    <Typography variant="body2" color="primary.700">
                      {mapping.file.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: '28rem' }}>
                    <Autocomplete
                      size="small"
                      options={assemblies}
                      getOptionLabel={(option) => option.tagName}
                      value={
                        assemblies.find((assembly) => assembly.id === mapping.assemblyId) ?? null
                      }
                      onChange={(_event, value) => updateMapping(index, value)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(parameters) => (
                        <TextField
                          {...parameters}
                          placeholder="Select assembly..."
                          error={mapping.assemblyId === null}
                          helperText={mapping.assemblyId === null ? 'Required' : undefined}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" marginTop="auto">
        <Button variant="outlined" onClick={goToPreviousStep}>
          Back
        </Button>
        <Button variant="contained" onClick={goToNextStep} disabled={!allMapped}>
          Next
        </Button>
      </Stack>
    </Stack>
  );
};
